import { get } from 'lodash';
import { appLog } from 'common/utils/logger';
import type { SupportedLanguage, DictionaryKeyResolverFn, StoryDictionaryMap } from './types';
import { DEFAULT_LANGUAGE, LANGUAGE_MAP, RTL_LANGUAGES, SUPPORTED_LANGUAGES } from './constants';

const logger = appLog.extend('i18n/utils');

/**
 * Check if language is RTL
 */
export const isRTLLanguage = (language: SupportedLanguage): boolean => {
	return RTL_LANGUAGES.includes(language);
};

/**
 * Get language direction
 */
export const getLanguageDirection = (language: SupportedLanguage): 'ltr' | 'rtl' => {
	return LANGUAGE_MAP.DIR[language];
};

/**
 * Get DOM lang attribute value
 */
export const getDomLangAttribute = (language: SupportedLanguage): string => {
	return LANGUAGE_MAP.DOM_LANG[language];
};

/**
 * Dictionary cache to avoid loading the same language multiple times
 * This is shared with the hook implementation
 */
export const dictionaryCache = new Map<SupportedLanguage, StoryDictionaryMap>();

/**
 * Dynamic dictionary loader - loads only the required language
 */
export const loadStoryDictionary = async (language: SupportedLanguage): Promise<StoryDictionaryMap> => {
	logger('loading dictionary for language: %s', language);

	// Check cache first
	if (dictionaryCache.has(language)) {
		logger('load dictionary - cache hit for language: %s', language);
		return dictionaryCache.get(language)!;
	}

	let dictionary: StoryDictionaryMap;

	try {
		// Dynamic import using template string - much cleaner than 40+ case statements
		const languageCode = language.toLowerCase();
		dictionary = (await import(`./dictionaries/${languageCode}`)).default;

		logger('loaded dictionary: %o', dictionary);

		// Cache the loaded dictionary
		dictionaryCache.set(language, dictionary);
		return dictionary;
	} catch {
		console.warn(`Failed to load dictionary for language: ${language}. Falling back to ${DEFAULT_LANGUAGE}.`);

		// Fallback to default language
		if (language !== DEFAULT_LANGUAGE) {
			return loadStoryDictionary(DEFAULT_LANGUAGE);
		}

		// If even default fails, return empty object with warning
		console.error('Critical: Failed to load default language dictionary');
		return {} as StoryDictionaryMap;
	}
};

/**
 * Get currently cached languages
 */
export const getCachedLanguages = (): SupportedLanguage[] => {
	return Array.from(dictionaryCache.keys());
};

/**
 * Clear dictionary cache (useful for testing or memory management)
 */
export const clearDictionaryCache = (): void => {
	dictionaryCache.clear();
};

/**
 * Get cache size for monitoring
 */
export const getDictionaryCacheSize = (): number => {
	return dictionaryCache.size;
};

/**
 * Template interpolation - replace {{key}} with values
 */
const interpolate = (text: string, values: Record<string, string | number>): string => {
	return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
		return values[key]?.toString() || match;
	});
};

export const createDictionaryKeyResolverFn =
	(dictionary: StoryDictionaryMap | null): DictionaryKeyResolverFn =>
	(key, interpolation = {}) => {
		if (!dictionary) {
			logger('no dictionary found for key: %s', key);
			return key;
		}

		let value = get(dictionary, key, key) as any;

		logger('resolving key: %s => %o', key, value);

		// Handle interpolation {{ variable }}
		if (interpolation && typeof value === 'string') {
			value = interpolate(value, interpolation);
		}

		return value;
	};

/**
 * Get human-readable language name from language code
 */
export const getLanguageName = (language: SupportedLanguage): string => {
	return LANGUAGE_MAP.DISPLAY_NAMES[language] || language;
};

/**
 * Get language options for select components
 */
export const getLanguageOptions = (current: SupportedLanguage) => {
	return SUPPORTED_LANGUAGES.map(code => ({
		value: code,
		label: LANGUAGE_MAP.DISPLAY_NAMES[code],
	}))
		.sort((a, b) => a.label.localeCompare(b.label))
		.filter(option => option.value !== current);
};
